import { computed, effect, inject, Injectable, Injector, signal, untracked } from '@angular/core';
import type {
  FrequencyFile,
  FrequencyFileOrigin,
  FrequencyWarning,
  SiteFileKind,
  SiteFrequencySetup,
} from '@wheere/api-site';
import { errorMessage } from '@wheere-front/shared-util';
import { SiteFrequencyClient } from '../data/site-frequency.client';
import { SitePreset } from '../data/site.model';
import {
  antennaCount,
  calibShape,
  FrequencyBlocker,
  FrequencyLayoutGateway,
  frequencySetupBlockers,
  generateSignals,
  readFileText,
  GeneratedSignalView,
  SiteAntennaView,
  siteAntennas,
} from '../util/frequency-file';
import { frequencyRejection, FrequencyRejectionView, rejectionLines } from '../util/frequency-rejection';
import { SITE_CONFIG_HOST } from './site-config-host';
import { SiteConfigurationService } from './site-configuration.service';

/** Fichier de fréquences affiché : brouillon importé, fichier en place, ou celui du preset choisi. */
export interface EffectiveFrequencyFile {
  fileName: string;
  content: FrequencyFile;
  source: 'draft' | 'saved' | 'preset';
  /** Fichier en place : déposé, copié d'un preset, ou converti par la migration V41. */
  origin?: FrequencyFileOrigin;
  /** Date d'import (brouillon : maintenant ; en place : `uploadedAt`). */
  importedAt?: string;
}

export interface EffectiveCalib {
  fileName: string;
  rows: number;
  columns: number;
  /** Texte du calib (aperçu, téléchargement) ; null tant qu'il n'est pas chargé. */
  raw: string | null;
  source: 'draft' | 'saved' | 'preset';
}

interface DraftFrequencyFile {
  fileName: string;
  raw: string;
  content: FrequencyFile;
  importedAt: string;
}

interface DraftCalib {
  fileName: string;
  raw: string;
  rows: number;
  columns: number;
}

/** Fichier refusé à l'import : le fichier précédent reste en place. */
export interface FrequencyImportRejection {
  kind: 'frequency' | 'calib';
  fileName: string;
  /** Motifs du serveur (un par ligne). */
  lines: string[];
  /** Ancienne grille .txt : renvoi vers Outils › Convertisseur. */
  legacyFormat: boolean;
}

/**
 * État du fichier de fréquences et du calib de l'onglet Configuration d'un site (inside,
 * ADR-039). Un import est contrôlé par le serveur sans stockage (`dryRun`, contre la disposition
 * en cours d'édition) ; accepté, il devient le brouillon, stocké à la validation
 * (`SiteConfigurationService.saveViaLayout`). Antennes A1..AN, signal.json générés et motifs de
 * blocage sont recalculés à chaque changement de la disposition.
 *
 * Fourni par l'onglet Configuration (une instance par montage), partagé par la section Fichiers et
 * la carte « signal.json générés ».
 */
@Injectable()
export class SiteFrequencyStore {
  readonly #injector = inject(Injector);
  /** Résolu à l'usage : en mode preset seul (integrator), aucun appel, aucun client HTTP requis. */
  get #client(): SiteFrequencyClient {
    return this.#injector.get(SiteFrequencyClient);
  }
  readonly #svc = inject(SiteConfigurationService);
  readonly #host = inject(SITE_CONFIG_HOST, { optional: true });

  /** Activé par la section Fichiers d'inside (jamais en mode preset seul de l'integrator). */
  readonly active = signal(false);
  readonly sourceMode = signal<'preset' | 'custom'>('custom');
  readonly preset = signal<SitePreset | null>(null);

  readonly setup = signal<SiteFrequencySetup | null>(null);
  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly #savedCalibRaw = signal<string | null>(null);
  readonly #draftFile = signal<DraftFrequencyFile | null>(null);
  readonly #draftCalib = signal<DraftCalib | null>(null);
  readonly rejection = signal<FrequencyImportRejection | null>(null);
  /** Avertissements du serveur au dernier import accepté (badges jaunes, messages). */
  readonly importWarnings = signal<FrequencyWarning[]>([]);
  readonly importing = signal<'frequency' | 'calib' | null>(null);

  readonly hasDraftFile = computed(() => !!this.#draftFile());
  readonly hasDraftCalib = computed(() => !!this.#draftCalib());

  /** Slug du site (nom des signal.json exportés). */
  readonly slug = computed(() => {
    const site = this.#host?.selected();
    return (site?.slug ?? site?.name ?? '').toUpperCase();
  });

  /** Disposition en cours d'édition : gateways dans l'ordre du site, RF OUT de chaque antenne. */
  readonly layout = computed<FrequencyLayoutGateway[]>(() => {
    const ants = this.#svc.gatewayAntennas();
    return this.#svc.selectedGateways().map((g, i) => ({
      name: g.name || `GW${i}`,
      deviceId: g.deviceId,
      antennas: (ants[g.id ?? ''] ?? []).map(a => ({ rfOut: a.rfOut })),
    }));
  });
  readonly antennaCount = computed(() => antennaCount(this.layout()));
  readonly size = computed(() => this.#svc.selectedSize()?.value ?? 0);

  readonly effectiveFile = computed<EffectiveFrequencyFile | null>(() => {
    if (this.sourceMode() === 'preset') {
      const p = this.preset();
      return p?.frequencyFile
        ? { fileName: p.frequencyFileName ?? 'frequences.json', content: p.frequencyFile, source: 'preset' }
        : null;
    }
    const draft = this.#draftFile();
    if (draft)
      return { fileName: draft.fileName, content: draft.content, source: 'draft', importedAt: draft.importedAt };
    const saved = this.setup()?.frequencyFile;
    return saved
      ? {
          fileName: saved.fileName,
          content: saved.content,
          source: 'saved',
          origin: saved.origin,
          importedAt: saved.uploadedAt ? String(saved.uploadedAt) : undefined,
        }
      : null;
  });

  readonly effectiveCalib = computed<EffectiveCalib | null>(() => {
    if (this.sourceMode() === 'preset') {
      const p = this.preset();
      if (!p?.calibFileName && !p?.calibRawContent) return null;
      const shape = p.calib ?? (p.calibRawContent ? calibShape(p.calibRawContent) : null);
      if (!shape) return null;
      return {
        fileName: p.calibFileName ?? 'calib.txt',
        rows: shape.rows,
        columns: shape.columns,
        raw: p.calibRawContent ?? null,
        source: 'preset',
      };
    }
    const draft = this.#draftCalib();
    if (draft) return { ...draft, source: 'draft' };
    const saved = this.setup()?.calib;
    if (!saved) return null;
    const raw = this.#savedCalibRaw();
    const shape =
      saved.rows != null && saved.columns != null
        ? { rows: saved.rows, columns: saved.columns }
        : raw
          ? calibShape(raw)
          : { rows: 0, columns: 0 };
    return { fileName: saved.fileName, rows: shape.rows, columns: shape.columns, raw, source: 'saved' };
  });

  readonly antennas = computed<SiteAntennaView[]>(() =>
    siteAntennas(this.layout(), this.effectiveFile()?.content ?? null, this.effectiveCalib()?.columns ?? null)
  );

  /** freq_delta en surplus (badge jaune « N ignorés »). */
  readonly ignoredDeltas = computed<number[]>(() => {
    const f = this.effectiveFile()?.content;
    return f ? f.freq_delta.slice(this.antennaCount()) : [];
  });

  readonly generated = computed<GeneratedSignalView[] | null>(() => {
    const f = this.effectiveFile()?.content;
    return f ? generateSignals(this.slug(), this.layout(), f, this.size()) : null;
  });

  readonly blockers = computed<FrequencyBlocker[]>(() => {
    const file = this.effectiveFile();
    const calib = this.effectiveCalib();
    const p = this.sourceMode() === 'preset' ? this.preset() : null;
    return frequencySetupBlockers({
      gateways: this.layout(),
      file: file?.content ?? null,
      fileName: file?.fileName ?? null,
      calib: calib ? { fileName: calib.fileName, rows: calib.rows, columns: calib.columns } : null,
      preset: p ? { name: p.name, maxAntennas: p.maxAntennas ?? null } : null,
    });
  });

  /** Site non converti par la migration V41 et sans nouveau fichier : bloqué, renvoi au Convertisseur. */
  readonly migrationFailure = computed(() => {
    if (this.sourceMode() === 'preset' || this.#draftFile()) return null;
    const s = this.setup();
    return s && !s.frequencyFile && s.migrationFailure ? s.migrationFailure : null;
  });

  constructor() {
    // État des fichiers pour la barre de préparation (inside seulement).
    effect(() => {
      if (!this.active()) return;
      const hasFile = !!this.effectiveFile();
      const hasCalib = !!this.effectiveCalib();
      const blocked = this.blockers().length > 0;
      untracked(() => {
        this.#svc.setFrequenciesLoaded(hasFile);
        this.#svc.setGlobalCalibLoaded(hasCalib);
        this.#svc.frequencyBlocked.set(blocked);
      });
    });
  }

  /** Charge l'état du site (fichier en place, calib, échec de migration) et le texte du calib. */
  async load(siteId: string): Promise<void> {
    this.loading.set(true);
    this.loadError.set(null);
    try {
      const [setup, files] = await Promise.all([
        this.#client.setup(siteId),
        this.#client.siteFiles(siteId).catch(() => []),
      ]);
      this.setup.set(setup);
      this.#savedCalibRaw.set(files.find(f => f.fileType === 'calib_file')?.rawContent ?? null);
    } catch (e) {
      // Jamais avalé : la section affiche l'erreur et la validation reste bloquée (aucun fichier).
      this.setup.set(null);
      this.loadError.set(errorMessage(e, 'Fichiers du site indisponibles'));
    } finally {
      this.loading.set(false);
    }
  }

  /** Après une validation réussie : les brouillons sont stockés, on relit l'état du site. */
  async afterSave(siteId: string): Promise<void> {
    this.#draftFile.set(null);
    this.#draftCalib.set(null);
    this.rejection.set(null);
    this.importWarnings.set([]);
    await this.load(siteId);
  }

  /**
   * Import d'un fichier de fréquences : contrôlé par le serveur sans stockage contre les antennes
   * de la disposition en cours. Refus : le fichier précédent reste en place, motifs affichés.
   */
  async importFrequencyFile(siteId: string, file: File): Promise<boolean> {
    return this.#import('frequency', file, async () => {
      const raw = await readFileText(file);
      const result = await this.#client.checkSiteFile(siteId, 'frequency_file', file, this.antennaCount());
      const content = result.frequencyFile ?? (JSON.parse(raw) as FrequencyFile);
      this.#draftFile.set({ fileName: file.name, raw, content, importedAt: new Date().toISOString() });
      return result.warnings ?? [];
    });
  }

  /**
   * Import d'un calib, contrôlé sans stockage. Avec un fichier de fréquences en brouillon (pas encore
   * stocké), ses lignes sont contrôlées contre le nombre de fréquences du brouillon (`frequencyCount`).
   */
  async importCalib(siteId: string, file: File): Promise<boolean> {
    return this.#import('calib', file, async () => {
      const raw = await readFileText(file);
      const draft = this.#draftFile();
      const kind: SiteFileKind = 'calib_file';
      const result = await this.#client.checkSiteFile(
        siteId,
        kind,
        file,
        this.antennaCount(),
        draft ? draft.content.freq.length : undefined
      );
      const shape = result.calib ?? calibShape(raw);
      this.#draftCalib.set({ fileName: file.name, raw, rows: shape.rows, columns: shape.columns });
      return result.warnings ?? [];
    });
  }

  /** Annule l'import en brouillon : le fichier en place redevient celui affiché. */
  discardDraft(kind: 'frequency' | 'calib'): void {
    if (kind === 'frequency') this.#draftFile.set(null);
    else this.#draftCalib.set(null);
    this.rejection.set(null);
  }

  /** Suppression du fichier en place (immédiate côté serveur), puis relecture de l'état du site. */
  async deleteSaved(siteId: string, kind: 'frequency' | 'calib'): Promise<string | null> {
    try {
      await this.#client.deleteSiteFile(siteId, kind === 'frequency' ? 'frequency_file' : 'calib_file');
      this.discardDraft(kind);
      await this.load(siteId);
      return null;
    } catch (e) {
      return errorMessage(e, 'Suppression impossible : réessayez.');
    }
  }

  /** Fichiers à stocker au save : les brouillons, en import personnalisé seulement. */
  draftFiles(): { frequency: DraftFrequencyFile | null; calib: DraftCalib | null } {
    if (this.sourceMode() === 'preset') return { frequency: null, calib: null };
    return { frequency: this.#draftFile(), calib: this.#draftCalib() };
  }

  async #import(kind: 'frequency' | 'calib', file: File, run: () => Promise<FrequencyWarning[]>): Promise<boolean> {
    this.importing.set(kind);
    try {
      const warnings = await run();
      this.rejection.set(null);
      this.importWarnings.set(warnings);
      this.#svc.markFilesChanged();
      return true;
    } catch (e) {
      const r: FrequencyRejectionView | null = frequencyRejection(e);
      this.rejection.set({
        kind,
        fileName: file.name,
        lines: r ? rejectionLines(r) : [errorMessage(e, 'Contrôle du fichier impossible : réessayez.')],
        legacyFormat: r?.legacyFormat ?? false,
      });
      return false;
    } finally {
      this.importing.set(null);
    }
  }
}
